import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, Calendar, Envelope, ArrowRight, House } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import usePageMeta from "@/hooks/usePageMeta";

/**
 * Public Success page — no auth required.
 * Handles the $97 Discovery Session flow when ?type=discovery is present.
 * For the legacy Provisions product flow (session_id only, no type param),
 * requires auth (wrapper handles that in App.js).
 */
export default function CheckoutSuccessPublic() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const type = params.get("type") || "discovery";
  const isDiscovery = type === "discovery";

  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  usePageMeta(
    "Thank You — Discovery Session Confirmed",
    "Your $97 Discovery Session is confirmed. We'll be in touch shortly to schedule your one-on-one call."
  );

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 15) { setStatus("timeout"); return; }
      attemptsRef.current += 1;
      try {
        const endpoint = isDiscovery
          ? `/create-checkout-session/status/${sessionId}`
          : `/checkout/status/${sessionId}`;
        const { data: d } = await api.get(endpoint);
        setData(d);
        if (d.payment_status === "paid") { setStatus("paid"); return; }
        if (d.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, isDiscovery]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 md:py-32 text-center" data-testid="discovery-success-page">
      {status === "checking" && (
        <>
          <div className="w-20 h-20 border border-[#FFD700] rotate-45 mx-auto mb-8 animate-pulse-gold flex items-center justify-center">
            <Calendar size={32} weight="duotone" className="text-[#FFD700] -rotate-45" />
          </div>
          <span className="overline">// Confirming Your Session</span>
          <h1 className="heading-display text-3xl md:text-4xl mt-3 mb-4">Confirming your payment...</h1>
          <p className="text-white/60 font-light">Just a moment while we finalize everything.</p>
        </>
      )}

      {status === "paid" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="discovery-success-paid">
          <CheckCircle size={80} weight="duotone" className="text-[#FFD700] mx-auto mb-6 animate-pulse-gold" />
          <span className="overline">// Payment Confirmed</span>
          <h1 className="heading-display text-4xl md:text-5xl mt-3 mb-6">
            Thank you — <span className="text-[#FFD700]">welcome aboard.</span>
          </h1>
          <p className="text-lg text-white/80 font-light leading-relaxed mb-4">
            Your <strong>$97 Discovery Session</strong> is confirmed. We're excited to work with you.
          </p>
          {data?.customer_email && (
            <div className="inline-flex items-center gap-2 text-sm text-white/60 font-light mb-8">
              <Envelope size={14} weight="duotone" className="text-[#FFD700]" />
              A receipt has been sent to <span className="text-white font-mono">{data.customer_email}</span>
            </div>
          )}

          {/* What happens next */}
          <div className="surface-card p-6 md:p-8 text-left mb-10 mt-10">
            <div className="overline mb-4">// What Happens Next</div>
            <ol className="space-y-4">
              {[
                { title: "Check your email", body: "You'll receive a confirmation with your receipt and a link to book your session time within the next few minutes." },
                { title: "Prepare a few notes", body: "Jot down your top 2–3 goals and one specific challenge you'd like to work through. That's it — we'll drive the rest." },
                { title: "Show up ready", body: "We'll spend the full session mapping your situation and identifying the highest-leverage next steps to move forward." },
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 border border-[#FFD700]/40 flex items-center justify-center flex-shrink-0 font-mono text-sm text-[#FFD700]">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-1">{s.title}</div>
                    <div className="text-sm text-white/60 font-light leading-relaxed">{s.body}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/" data-testid="success-home-btn" className="btn-gold px-8 py-4 text-xs inline-flex items-center gap-3">
              <House size={16} /> Return Home
            </Link>
            <Link to="/catalog" data-testid="success-catalog-btn" className="btn-ghost px-8 py-4 text-xs inline-flex items-center gap-3">
              Explore Provisions <ArrowRight size={14} />
            </Link>
          </div>
        </motion.div>
      )}

      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <span className="overline text-red-400">// Payment Not Confirmed</span>
          <h1 className="heading-display text-3xl md:text-4xl mt-3 mb-4">We couldn't confirm your payment</h1>
          <p className="text-white/60 font-light mb-8">
            {status === "expired" ? "The checkout session has expired." : "There was a delay verifying your payment. If you were charged, we've received it — please check your email."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/" className="btn-gold px-8 py-4 text-xs">Return Home</Link>
            <a href="mailto:support@eigooinc.net" className="btn-ghost px-8 py-4 text-xs">Contact Support</a>
          </div>
        </>
      )}
    </div>
  );
}
